import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import snakeCase from 'lodash/snakeCase';

import Input from 'components/Input';
import RadioButton from '../RadioButton';
import Dropdown from '../Dropdown';

import { isRequiredNumber } from 'utilities/validators.js';

import {
  EQUAL_PROPORTION,
  NON_EQUAL_PROPORTION
} from 'scenes/InferentialStatistics/Constants';

const MAX_CATEGORY_LABEL_LENGTH = 15;

const truncateFieldLabel = levelName =>
  levelName.length > MAX_CATEGORY_LABEL_LENGTH
    ? `${levelName.slice(0, MAX_CATEGORY_LABEL_LENGTH)}...`
    : levelName;

const ChiSquareComponent = props => {
  const {
    totalCount,
    parameterLevels,
    expectedCountRadioValue,
    optionsObservedCounts,
    parameter,
    onExpectedCountsTypeChange,
    calculatedTotalValue,
    setValidationChiSquareError,
    isValidationChiSquareError
  } = props;

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const handleAlertTriangleMouseEnter = () => setIsPopupVisible(true);

  const handleAlertTriangleMouseLeave = () => setIsPopupVisible(false);

  const isEqualProportionSelected =
    expectedCountRadioValue === EQUAL_PROPORTION;

  useEffect(() => {
    const minCount = totalCount - 1;
    const maxCount = totalCount + 1;
    if (calculatedTotalValue > maxCount || calculatedTotalValue < minCount) {
      setValidationChiSquareError(true);
    } else {
      setValidationChiSquareError(false);
    }
  }, [calculatedTotalValue]);

  return (
    <>
      <Dropdown
        optionsList={optionsObservedCounts}
        label="Variable to Test"
        name="observedCounts"
        placeholder="Select a variable to test "
        id="observedCountsDropdown"
      />
      <div data-testid="nullHypothesisSwitch">
        <p className="main-dropdown__label width-full">
          Expected counts under the Null Hypothesis
        </p>
        <div className="mr-20 inline-block">
          <RadioButton
            value="equalProportion"
            name="expectedCounts"
            labelClassName="mr-10"
            onChange={onExpectedCountsTypeChange}
          >
            Equal
          </RadioButton>
        </div>
        <RadioButton
          value="nonEqualProportion"
          name="expectedCounts"
          onChange={onExpectedCountsTypeChange}
        >
          Non-Equal
        </RadioButton>
        <div className="flex flex-wrap mt-20">
          <span className="width-60 fs-14 bold mb-5">Category</span>
          <span className="width-40 fs-14 bold mb-5">Expected Count</span>
          {parameterLevels.map(level => {
            const levelName = snakeCase(level.name);
            return (
              <Field
                type="number"
                key={level.name}
                id={`level_${levelName}`}
                component={Input}
                name={`${parameter}.level_${levelName}`}
                label={truncateFieldLabel(level.name)}
                pattern="\d+"
                wrapperClassName={`flex align-center width-full mb-10 relative ${
                    isValidationChiSquareError && 'has-error'
                  }`}
                inputClassName="main-input__input width-40"
                labelClassName="main-input__label width-60"
                validate={isEqualProportionSelected ? null : isRequiredNumber}
                disabled={isEqualProportionSelected}
              />
            );
          })}
        </div>
        <hr className="separationLine" />
        <div className="flex">
          <span className="width-60 fs-14 bold mb-5">Total (n)</span>
          <span
            className={`width-40 fs-14 bold mb-5 pl-10 ${
                isValidationChiSquareError && 'in-red-500'
              }`}
            data-testid="totalCount"
          >
            {totalCount}
            {isValidationChiSquareError && (
              <span className="in-red-500 ml-5">
                <i
                  className="icon icon-alert-triangle"
                  onMouseEnter={handleAlertTriangleMouseEnter}
                  onMouseLeave={handleAlertTriangleMouseLeave}
                />
                {isPopupVisible && (
                  <span className="absolute main-tooltip">
                    Expected Counts must sum to {totalCount}.
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
      </div>
    </>
  );
};

ChiSquareComponent.propTypes = {
  optionsObservedCounts: PropTypes.array.isRequired,
  parameterLevels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      levels: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          count: PropTypes.number.isRequired
        })
      ),
      proportion: PropTypes.number.isRequired
    })
  ).isRequired,
  expectedCountRadioValue: PropTypes.oneOf([
    EQUAL_PROPORTION,
    NON_EQUAL_PROPORTION
  ]),
  totalCount: PropTypes.number.isRequired,
  parameter: PropTypes.string.isRequired,
  onExpectedCountsTypeChange: PropTypes.func.isRequired,
  calculatedTotalValue: PropTypes.number.isRequired,
  isValidationChiSquareError: PropTypes.bool.isRequired,
  setValidationChiSquareError: PropTypes.func.isRequired
};

export default ChiSquareComponent;
